import React, { useState } from 'react';
import type { User } from 'firebase/auth';
import { startGoogleSecureLogin, resolveDeviceChallenge, resolveTotpChallenge, resendGoogleDeviceCode } from '../../services/googleSecureAuthService';
import type { AuthChallenge } from '../../services/secureAuthShared';
import { SecureChallengeModal } from './SecureChallengeModal';
import { trackEvent } from '../../utils/gtag';
import { trackOpenAiTrialStarted } from '../../utils/oaiq';

interface GoogleSecureLoginButtonProps {
  text: string;
  className?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  redirectTo?: string;
}

/**
 * Remplace GoogleButtonAlt pour le LOGIN (src/components/auth/LoginForm.tsx
 * uniquement pour l'instant — inscription/partenaires/affiliés restent sur
 * l'ancien flow signInWithPopup, voir GoogleButtonAlt.tsx, hors scope de ce
 * durcissement). Ne signe JAMAIS dans Firebase tant que le serveur n'a pas
 * validé l'identité Google et, si requis, l'appareil + la 2FA — voir
 * src/services/googleSecureAuthService.ts.
 */
export function GoogleSecureLoginButton({ text, className, onSuccess, onError, redirectTo }: GoogleSecureLoginButtonProps) {
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<{ pendingSessionId: string; challenge: AuthChallenge } | null>(null);

  const finish = (user: User, isNewUser: boolean, onboardingCompleted: boolean) => {
    if (isNewUser) {
      trackEvent('signup_completed', { method: 'google' });
      trackOpenAiTrialStarted(user.uid);
    }
    setLoading(false);
    setPending(null);
    if (onSuccess) { onSuccess(); return; }
    if (redirectTo) { window.location.href = redirectTo; return; }
    if (isNewUser || onboardingCompleted === false) {
      window.location.href = '/onboarding';
    } else {
      window.location.href = '/dashboard';
    }
  };

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const result = await startGoogleSecureLogin();
      if (result.status === 'authenticated') {
        finish(result.user, result.isNewUser, result.onboardingCompleted);
      } else {
        setPending({ pendingSessionId: result.pendingSessionId, challenge: result.challenge });
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
      if (onError) onError(new Error(error.message || 'Erreur de connexion Google'));
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className={`flex items-center justify-center gap-2 w-full py-3 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${className || 'bg-[#0a0a0d] hover:bg-[#0a0a0d] text-white border border-brand-primary/30 transition-colors'}`}
      >
        {loading ? (
          <svg className="animate-spin h-5 w-5" style={{ color: 'inherit' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
            <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
            <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
            <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
          </svg>
        )}
        <span className="font-medium" style={{ color: 'inherit' }}>{loading ? 'Connexion en cours...' : text}</span>
      </button>

      {pending && (
        <SecureChallengeModal
          pendingSessionId={pending.pendingSessionId}
          challenge={pending.challenge}
          resolveDevice={resolveDeviceChallenge}
          resolveTotp={resolveTotpChallenge}
          resendDeviceCode={resendGoogleDeviceCode}
          onSuccess={(user) => finish(user, false, true)}
          onCancel={() => setPending(null)}
        />
      )}
    </>
  );
}

export default GoogleSecureLoginButton;
